import React, { useState, useEffect } from 'react';
import { Layers, Search, X, Plus, Loader, Copy } from 'lucide-react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { getOrgSlug, getApiBaseUrl } from '../config/backend.config';
import './WorkspaceSelectionModal.css';

function WorkspaceSelectionModal({ isOpen, onClose, onWorkspaceSelect, onCloneSharedWorkspace }) {
  // Workspace state
  const [workspaces, setWorkspaces] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Loading states
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);

  // Create workspace state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [creationError, setCreationError] = useState(null);

  const { organization } = useOrganization();
  const clerk = useClerk();

  // Fetch user workspaces when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setShowCreateForm(false);
      setNewWorkspaceName('');
      setCreationError(null);
      fetchWorkspaces();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredWorkspaces = workspaces.filter(ws =>
    (ws.workspace_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fetch workspaces from backend
  const fetchWorkspaces = async () => {
    try {
      setLoadingWorkspaces(true);

      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // Fetch user workspaces (no org_id/user_id needed - inferred from auth)
      const workspacesUrl = `${apiBaseUrl}/workspaces`;
      console.log('Fetching workspaces from:', workspacesUrl);

      const workspacesResponse = await fetch(workspacesUrl, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (workspacesResponse.ok) {
        const workspaceData = await workspacesResponse.json();
        console.log('Workspaces loaded:', workspaceData);
        setWorkspaces(workspaceData);
      } else {
        console.error('Failed to fetch workspaces:', workspacesResponse.status, workspacesResponse.statusText);
        const error = await workspacesResponse.text();
        console.error('Error details:', error);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  // Create new workspace
  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      setCreationError('Please enter a workspace name');
      return;
    }

    setCreatingWorkspace(true);
    setCreationError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const createUrl = `${apiBaseUrl}/workspaces`;
      console.log('Creating workspace at:', createUrl);

      const response = await fetch(createUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_name: newWorkspaceName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Creation failed: ${response.statusText}`);
      }

      const createdWorkspace = await response.json();
      console.log('Workspace created:', createdWorkspace);

      // Reset form
      setShowCreateForm(false);
      setNewWorkspaceName('');

      // Notify parent component with workspace context
      if (onWorkspaceSelect) {
        onWorkspaceSelect(createdWorkspace);
      }
      onClose();
    } catch (error) {
      console.error('Creation error:', error);
      setCreationError(error.message);
    } finally {
      setCreatingWorkspace(false);
    }
  };

  // Select existing workspace
  const handleWorkspaceSelect = async (workspace) => {
    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // Fetch fresh workspace data to get updated is_owner flag
      const workspaceUrl = `${apiBaseUrl}/workspaces/${workspace.workspace_id}`;
      const response = await fetch(workspaceUrl, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch workspace: ${response.statusText}`);
      }

      const freshWorkspace = await response.json();

      // If this is a shared workspace (not owned), trigger clone flow instead
      if (!freshWorkspace.is_owner) {
        if (onCloneSharedWorkspace) {
          onCloneSharedWorkspace(freshWorkspace);
        }
        onClose();
        return;
      }

      // Notify parent component with fresh workspace data
      if (onWorkspaceSelect) {
        onWorkspaceSelect(freshWorkspace);
      }

      onClose();
    } catch (error) {
      console.error('Error selecting workspace:', error);
      alert(`Failed to select workspace: ${error.message}`);
    }
  };


  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const handleClose = () => {
    if (!creatingWorkspace) {
      setShowCreateForm(false);
      setNewWorkspaceName('');
      setCreationError(null);
      onClose();
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewWorkspaceName('');
    setCreationError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="template-modal-overlay" onClick={handleClose}>
      <div className="template-modal" onClick={(e) => e.stopPropagation()}>
        <div className="template-modal-header">
          <div className="template-modal-title">
            <Layers size={20} />
            <h2>Workspaces</h2>
          </div>
          <button
            className="template-modal-close"
            onClick={handleClose}
            disabled={creatingWorkspace}
          >
            <X size={20} />
          </button>
        </div>

        <div className="template-modal-content">
          {/* Create New Workspace - Two step process */}
          <div className="workspace-create-section">
            {!showCreateForm ? (
              <button
                className="workspace-create-trigger"
                onClick={() => setShowCreateForm(true)}
              >
                <Plus size={18} />
                Create New Workspace
              </button>
            ) : (
              <div className="workspace-create-form">
                <input
                  type="text"
                  placeholder="Enter workspace name..."
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  className="workspace-create-input"
                  disabled={creatingWorkspace}
                  maxLength={100}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newWorkspaceName.trim()) {
                      handleCreateWorkspace();
                    } else if (e.key === 'Escape') {
                      handleCancelCreate();
                    }
                  }}
                />
                <div className="workspace-create-actions">
                  <button
                    className="workspace-create-cancel"
                    onClick={handleCancelCreate}
                    disabled={creatingWorkspace}
                  >
                    Cancel
                  </button>
                  <button
                    className="workspace-create-confirm"
                    onClick={handleCreateWorkspace}
                    disabled={creatingWorkspace || !newWorkspaceName.trim()}
                  >
                    {creatingWorkspace ? (
                      <>
                        <Loader size={14} className="spinner" />
                        Creating...
                      </>
                    ) : (
                      'Create'
                    )}
                  </button>
                </div>
                {creationError && (
                  <div className="workspace-error-message">
                    {creationError}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Search */}
          <div className="template-search-section">
            <div className="template-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search workspaces..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="template-search-input"
                disabled={creatingWorkspace}
              />
            </div>
          </div>

          {/* Workspace List */}
          <div className="template-list-section">
            {loadingWorkspaces ? (
              <div className="template-loading">
                <div className="loading-spinner"></div>
                <span>Loading workspaces...</span>
              </div>
            ) : filteredWorkspaces.length === 0 ? (
              <div className="template-empty">
                <Layers size={32} />
                <p>{workspaces.length === 0 ? 'No workspaces yet' : 'No matching workspaces'}</p>
                <span>{workspaces.length === 0 ? 'Create your first workspace above' : 'Try a different search term'}</span>
              </div>
            ) : (
              <div className="workspace-list">
                {filteredWorkspaces.map((workspace) => (
                  <div
                    key={workspace.workspace_id}
                    className={`workspace-item ${!workspace.is_owner ? 'workspace-item-shared' : ''}`}
                    onClick={() => handleWorkspaceSelect(workspace)}
                  >
                    <span className="workspace-item-name">
                      {workspace.workspace_name}
                    </span>
                    <span className="workspace-item-meta">
                      {workspace.is_owner ? (
                        <span className="workspace-badge-owner">Owner</span>
                      ) : (
                        <span className="workspace-badge-clone">
                          <Copy size={12} />
                          Clone from {workspace.owner_name}
                        </span>
                      )}
                      <span className="workspace-item-date">
                        {formatDate(workspace.created_at)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

export default WorkspaceSelectionModal;
